import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, User, ChevronRight, Youtube } from 'lucide-react';
import { Button } from '../components/ui/button';
import { searchUrl, CYBER_GUY_CHANNEL_URL } from '../data/videos';
import SEO, { SITE, breadcrumb, orgRef, personRef } from '../components/SEO';

const CATEGORIES = [
  'All',
  'Cybersecurity Basics',
  'Identity & Access',
  'Vulnerability Management',
  'Data Protection',
  'Security Operations',
  'Business Security',
  'Security Awareness',
  'Real-World Cyber Incidents',
];

const INSIGHTS = [
  {
    id: 'dlp-fim-discovery-first',
    title: 'Why DLP and FIM Deployments Fail: A Discovery-First Approach',
    author: 'Kenny Vic',
    date: 'January 2026',
    category: 'Data Protection',
    readTime: '4 min read',
    excerpt:
      "One pattern I've seen repeatedly with DLP and FIM: they're deployed for compliance, not for reality.",
    relatedVideo: 'Data Loss Prevention',
    relatedService: { slug: 'data-protection-backup', name: 'Data Protection & Backup' },
    body: [
      "One pattern I've seen repeatedly with DLP and FIM: they're deployed for compliance, not for reality.",
      "Before these tools become effective, sensitive data locations are often unclear, policies are too broad, alerts get ignored, and FIM produces noise with no ownership or context.",
      "The tools weren't the problem. The lack of data understanding and operational alignment was.",
      "The discovery-first approach identifies where sensitive data actually lives, classifies it by business impact, maps who accesses what and why, and separates high-risk systems from low-risk noise.",
      "Enforcement then becomes targeted: DLP rules instead of blanket blocks, FIM alerts tied to critical paths, clear ownership and escalation, and evidence-ready logging.",
      "The lesson: DLP and FIM don't protect data by existing. They protect data when they reflect how the business actually operates.",
    ],
  },
  {
    id: 'pam-unavoidable',
    title: 'Why Privileged Access Management Became Unavoidable',
    author: 'Kenny Vic',
    date: 'January 2026',
    category: 'Identity & Access',
    readTime: '3 min read',
    excerpt:
      'During incidents and audits, one question kept coming up: who owns this privileged account?',
    relatedVideo: 'Privileged Access Management (PAM)',
    relatedService: { slug: 'identity-access-security', name: 'Identity & Access Security' },
    body: [
      "During incidents and audits, one question kept coming up: who owns this privileged account?",
      "Privileged accounts had unclear ownership. Some passwords were shared and not consistently rotated. Usage tracking was limited.",
      "When incidents happened, attribution was difficult. When auditors asked for evidence, clarity was hard to provide.",
      "PAM was not introduced as a tool upgrade. It was introduced to restore accountability, traceability and audit confidence.",
      "If you cannot clearly explain who accessed what, when, and how — PAM stops being optional; it becomes foundational.",
    ],
  },
  {
    id: 'ad-not-pam-ready',
    title: 'Most Active Directories Are Not PAM-Ready',
    author: 'Kenny Vic',
    date: 'January 2026',
    category: 'Identity & Access',
    readTime: '3 min read',
    excerpt:
      'One lesson PAM deployments surface very quickly: PAM does not fix identity problems. It exposes them.',
    relatedVideo: 'Least Privilege in Plain English',
    relatedService: { slug: 'identity-access-security', name: 'Identity & Access Security' },
    body: [
      "One lesson PAM deployments surface very quickly: PAM does not fix identity problems. It exposes them.",
      "Stale privileged users. Unclear account ownership. Inconsistent permissions. Group structures that made sense to no one.",
      "These issues existed long before PAM. PAM simply removed the ability to hide them.",
      "If your directory is messy, PAM onboarding will be painful. If your directory is clean, PAM becomes significantly easier.",
      "Treat Active Directory as a core dependency of PAM, not a side task. PAM succeeds when identity hygiene is taken seriously.",
    ],
  },
];

const InsightsPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [category, setCategory] = useState('All');

  const filtered = useMemo(
    () => (category === 'All' ? INSIGHTS : INSIGHTS.filter((i) => i.category === category)),
    [category]
  );

  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/insights"
        title="Cybersecurity Insights | Kennyvicops Cyber Solutions Inc."
        description="Practical lessons from real-world cybersecurity challenges — short write-ups on what actually works when businesses try to strengthen their security."
        jsonLd={[
          breadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Insights', path: '/insights' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Cybersecurity Insights',
            url: `${SITE}/insights`,
            publisher: orgRef,
            blogPost: INSIGHTS.map((i) => ({
              '@type': 'BlogPosting',
              headline: i.title,
              description: i.excerpt,
              datePublished: '2026-01-01',
              author: personRef,
              publisher: orgRef,
              url: `${SITE}/insights#${i.id}`,
              articleSection: i.category,
            })),
          },
        ]}
      />

      {/* HERO — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(22,135,255,0.16),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-20">
          <div className="inline-flex items-center gap-2.5 mb-4">
            <span className="w-6 h-px bg-cyan-300/70" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">Cybersecurity Insights</span>
          </div>
          <h1 className="font-editorial text-4xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-white">
            Practical lessons from real-world cybersecurity challenges.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-3xl">
            Short, honest write-ups on what actually works &mdash; and what doesn&apos;t &mdash; when businesses try to strengthen their security.
          </p>
        </div>
      </section>

      {/* FILTERS + ARTICLES — off-white */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 border-b border-slate-200/70">
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`insight-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-4 py-2 text-sm rounded-full border transition-colors font-medium ${
                  category === c
                    ? 'bg-[#06101F] border-[#06101F] text-white'
                    : 'bg-white border-slate-200 text-[#475467] hover:border-blue-500/50 hover:text-blue-700'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 space-y-8">
          {filtered.map((i) => (
            <article
              key={i.id}
              id={i.id}
              className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-10 hover:shadow-lg hover:shadow-slate-900/5 transition-shadow"
              data-testid={`insight-${i.id}`}
            >
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-5">
                <span className="px-3 py-1 bg-blue-50 border border-blue-100 text-blue-700 rounded-full font-semibold text-[11px] uppercase tracking-[0.18em]">
                  {i.category}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> {i.date}
                </span>
                <span>{i.readTime}</span>
              </div>
              <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] mb-3 leading-[1.15]">
                {i.title}
              </h2>
              <div className="flex items-center gap-2 text-slate-500 text-sm mb-6">
                <User className="w-4 h-4" /> By {i.author}
              </div>
              <div className="space-y-4 text-[#475467] text-base sm:text-lg leading-relaxed">
                {i.body.map((p, idx) => (<p key={idx}>{p}</p>))}
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <a
                  href={searchUrl(i.relatedVideo)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-4 bg-[#F5F8FC] border border-slate-200 hover:border-blue-500/50 hover:bg-white rounded-xl transition-all"
                  data-testid={`insight-${i.id}-video`}
                >
                  <Youtube className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-0.5">Related Video</p>
                    <p className="text-sm text-[#101828] font-medium">{i.relatedVideo}</p>
                  </div>
                </a>
                <Link
                  to={`/services/${i.relatedService.slug}`}
                  className="flex items-center gap-3 p-4 bg-[#F5F8FC] border border-slate-200 hover:border-blue-500/50 hover:bg-white rounded-xl transition-all"
                  data-testid={`insight-${i.id}-service`}
                >
                  <ChevronRight className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-0.5">Related Service</p>
                    <p className="text-sm text-[#101828] font-medium">{i.relatedService.name}</p>
                  </div>
                </Link>
              </div>

              <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-sm text-[#475467]">
                  Facing something similar? Let&apos;s talk about it.
                </p>
                <Link to="/contact">
                  <Button className="px-6 py-5 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold">
                    Request a Security Consultation
                  </Button>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CHANNEL CTA — dark navy */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.2),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-editorial text-3xl sm:text-5xl font-semibold text-white leading-[1.1]">
            Prefer to watch, not read?
          </h2>
          <p className="mt-5 text-slate-300/95 text-base sm:text-lg max-w-2xl mx-auto">
            Every insight has a companion explainer on The Cyber Guy channel.
          </p>
          <div className="mt-8">
            <a href={CYBER_GUY_CHANNEL_URL} target="_blank" rel="noopener noreferrer">
              <Button className="px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold">
                <Youtube className="w-4 h-4 mr-2" /> Visit The Cyber Guy <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
